import {
    Box,
    Card,
    ClickAwayListener,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { Info, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import GetDailyBlockCounts from "src/api/GetDailyBlockCounts";
import GetSubAccountFinancialsReport from "src/api/GetSubAccountFinancialsReport";
import getHashrateUnit, { HASHRATE } from "src/api/HashrateFormatter";
import TablePaginationComp from "src/components/TablePaginationComp";
import useAuth from "src/hooks/useAuth";
import { applySorting, getComparator } from "src/utils/filterAndSortData";
import PPLNSCalculationBreakdown from "src/views/calculator/PPLNSCalculationBreakdown";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()(theme => ({
    table: {
        minWidth: 650
    },
    calculationInfo: {
        fontSize: 14,
        cursor: 'pointer',
        color: 'rgb(96, 165, 250)',
        '&:hover': {
            color: 'rgb(147 197 253 / var(--tw-text-opacity, 1));'
        },
    },
}));

const AuditReportTable = ({ startDate, endDate, subAccounts, saveExportData }) => {
    const { getAccessToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const { classes } = useStyles();
    const theme = useTheme();
    const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [error, setError] = useState('');
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('startDate');
    const [showDifficultyChangeInfo, setShowDifficultyChangeInfo] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    var sortedRows = applySorting(rows, getComparator(order, orderBy));
    const [openPplnsCalcDialog, setOpenPplnsCalcDialog] = useState(false);
    const [selectedPplnsDetailsDate, setSelectedPplnsDetailsDate] = useState(null);

    const displayDifficultyChangeInfo = (row, display) => {
        setSelectedRow(row);
        setShowDifficultyChangeInfo(display);
    }

    const handleOpenPplnsCalculationDialog = (date) => {
        const formattedDate = date.split(' ')[0];
        setSelectedPplnsDetailsDate(dayjs(formattedDate).utc().startOf("day"));
        setOpenPplnsCalcDialog(true);
    }

    const handleClosePplnsCalculationDialog = (date) => {
        setSelectedPplnsDetailsDate(null);
        setOpenPplnsCalcDialog(false);
        setSelectedPplnsDetailsDate(null);
    }

    // Zcash halving events (ZEC-specific)
    const HALVING_EVENTS = [
        { date: new Date('2016-10-28'), height: 0, subsidy: 12.5 },
        { date: new Date('2020-11-18'), height: 1050000, subsidy: 6.25 },
        { date: new Date('2024-11-23'), height: 2100000, subsidy: 3.125 },
        { date: new Date('2028-11-25'), height: 3150000, subsidy: 1.5625 }, // estimated
        { date: new Date('2032-11-26'), height: 4200000, subsidy: 0.78125 }, // estimated
    ];

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleColumnSort = tableColumn => {
        const isAsc = orderBy === tableColumn && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(tableColumn)
    }

    const getSubsidyFromLookup = (date) => {
        const auditDate = new Date(date.split(' ')[0]);
        // Find the most recent halving before the given date
        for (let i = HALVING_EVENTS.length - 1; i >= 0; i--) {
            if (auditDate >= HALVING_EVENTS[i].date) {
                return HALVING_EVENTS[i].subsidy;
            }
        }
        return 12.5; // Before first halving (shouldn't happen for most cases)
    };

    const formatHashrate = hashrate => {
        const hashrateUnit = getHashrateUnit(hashrate, HASHRATE.MSOLS);
        return hashrateUnit.formatToHashrateString((hashrate * HASHRATE.MSOLS.one).toFixed(3));
    }

    const handleUnitCoversion = (hashrate, hashrateUnit) => {
        //TODO convert to handle SOLs
        switch (hashrateUnit) {
            case "GH/s":
                return 1000000000 * hashrate
            case "TH/s":
                return 1000000000000 * hashrate
            case "PH/s":
                return 1000000000000000 * hashrate
            case "EH/s":
                return 1000000000000000000 * hashrate
            default:
                break;
        }
    }

    const handleAggShareDiffCalc = (hashrate) => {
        const hashrateValue = +hashrate.split(' ')[0];
        const hashrateUnit = hashrate.split(' ')[1];

        const convertedHashrate = handleUnitCoversion(hashrateValue, hashrateUnit)
        return {
            aggregatedDifficulty: (convertedHashrate / (2 ** 32)) * 86400,
            formattedAggregatedDifficulty: ((convertedHashrate / (2 ** 32)) * 86400).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
        }
    }

    const fetchData = useCallback(async () => {
        if (subAccounts.length) {
            const { tokenAvailable, accessToken } = await getAccessToken();
            if (!tokenAvailable) {
                console.error(accessToken)
                return;
            }

            setLoading(true);
            Promise.all([
                GetSubAccountFinancialsReport(startDate, endDate, subAccounts, 'byDay', accessToken),
                axios.get(`https://mempool.reddfive.com/api/v1/mining/difficulty-adjustments/`),
                GetDailyBlockCounts(dayjs(startDate).utc().valueOf(), dayjs(endDate).utc().valueOf(), accessToken)
            ]).then(([financials, difficultyData, { data: blocksData }]) => {
                // Pre-process and sort difficulty data
                const sortedDifficulties = difficultyData.data
                    .map(difficulty => ({
                        timestamp: difficulty[0] * 1000, // Convert to milliseconds
                        date: dayjs.unix(difficulty[0]).format('YYYY-MM-DD'),
                        difficulty: difficulty[2]
                    }))
                    .sort((a, b) => b.timestamp - a.timestamp);

                const auditData = financials.data?.map((financial, index) => {
                    const convertedHashrate = formatHashrate(financial.hashrateAccepted);
                    const startDateUnixMs = new Date(financial.startDate).getTime();
                    const startDate = financial.startDate?.split(' ')[0];

                    // Find the most recent difficulty adjustment on or before this date
                    const currentDifficulty = sortedDifficulties.find(
                        d => d.timestamp <= startDateUnixMs
                    );

                    const calculatedNetworkDifficulty = currentDifficulty?.difficulty;
                    const difficultyChanged = !!sortedDifficulties?.length && sortedDifficulties.map(d => d.date).includes(startDate);
                    const aggregatedDifficultyInfo = handleAggShareDiffCalc(convertedHashrate);
                    const blocksUsedForFpps = dayjs(financial.startDate).isBefore(dayjs('2023/11/30')) ? blocksData[startDate] : blocksData[startDate] - 6;

                    return {
                        ...financial,
                        year: Number(dayjs(financial.startDate).get('year').toString().slice(2)), // display last two digits of year
                        aggregateShareDifficulty: convertedHashrate === '0 H/s' ? 0 : aggregatedDifficultyInfo.aggregatedDifficulty,
                        formattedAggregatedDifficulty: convertedHashrate === '0 H/s' ? Number(0).toFixed(3) : aggregatedDifficultyInfo.formattedAggregatedDifficulty,
                        convertedHashrate: financial.convertedHashrate,
                        networkDifficulty: calculatedNetworkDifficulty,
                        networkDifficultyFormatted: calculatedNetworkDifficulty?.toLocaleString('en-US', { minimumFractionDigits: 2 }),
                        networkDifficultyChanged: difficultyChanged,
                        blockSubsidy: getSubsidyFromLookup(financial.startDate),
                        ppsBaseAmount: financial.ppsBaseAmount,
                        totalNetworkBlocks: !!startDate && !!Object.keys(blocksData).length && blocksData[startDate],
                        totalBlocksUsed: !!Object.keys(blocksData).length && blocksUsedForFpps,
                        fppsRatePercent: financial.fppsRatePercent,
                        txFeeRewardAmount: financial.txFeeRewardAmount,
                        poolFeeAmount: financial.poolFeeAmount,
                        earned: financial.earned
                    }
                });

                if (!!auditData.length) {
                    const csvExport = auditData.map((acct) => {
                        return {
                            'Year': acct.year,
                            'Start Date\n(UTC)': dayjs(acct.startDate).format("YYYY-MM-DD"),
                            'End Date\n(UTC)': dayjs(acct.endDate).format("YYYY-MM-DD"),
                            'Group': acct.groupName,
                            'Sub-\nAccount': acct.subAccountName,
                            'Accepted\nAggregate\nShare\nDifficulty': acct.aggregateShareDifficulty,
                            'Hashrate\n(Sol/s)': acct.convertedHashrate,
                            'Network\nDifficulty': acct.networkDifficulty,
                            'PPS Base\n(ZEC)': acct.ppsBaseAmount,
                            'Total\nNetwork\nBlocks': acct.totalNetworkBlocks,
                            'Total\nBlocks\nUsed': acct.totalBlocksUsed,
                            'PPLNS\nRate\n(%)': `${acct.fppsRatePercent}%`,
                            'Tx Fee\nReward\n(ZEC)': acct.txFeeRewardAmount,
                            'Gross\nEarnings\n(BTC)': acct.grossEarnings,
                            'Fee\nAmount\n(ZEC)': acct.poolFeeAmount,
                            'Total Amount (ZEC)': acct.earned,
                        }
                    });
                    saveExportData(csvExport);
                }

                setRows(auditData);
                setLoading(false);
            }).catch((error) => {
                console.error("Error: ", error.message);
                const errorMsg = !!error?.message ? error?.message : error;
                setError(errorMsg);
                setLoading(false);
            });
            setPage(0);
        } else {
            setRows([]);
            setLoading(false);
            setPage(0);
        }

    }, [startDate, endDate, subAccounts.length])

    useEffect(() => {
        fetchData();
    }, [fetchData])

    return (<>
        <Card
            style={{
                backgroundColor: 'var(--neutral-2)',
            }}
        >
            <>
                <TableContainer
                    style={{
                        backgroundColor: 'var(--neutral-2)',
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0,
                        borderTop: '2px solid var(--color-border)',
                        position: 'relative',
                        maxHeight: 'calc(100vh - 485px)',
                    }}
                    component={Paper}
                >
                    <Table className={classes.table} size="small" stickyHeader>
                        <TableHead>
                            <TableRow
                                style={{
                                    height: '50px',
                                    whiteSpace: 'nowrap',
                                    backgroundColor: 'var(--color-surface-raised)'
                                }}
                            >
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'year'}
                                        direction={orderBy === 'year' ? order : 'asc'}
                                        onClick={() => handleColumnSort('year')}>
                                        Year
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'startDate'}
                                        direction={orderBy === 'startDate' ? order : 'asc'}
                                        onClick={() => handleColumnSort('startDate')}>
                                        {t('startTime')} (UTC)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'endDate'}
                                        direction={orderBy === 'endDate' ? order : 'asc'}
                                        onClick={() => handleColumnSort('endDate')}>
                                        {t('endTime')} (UTC)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'groupName'}
                                        direction={orderBy === 'groupName' ? order : 'asc'}
                                        onClick={() => handleColumnSort('groupName')}>
                                        {t('group')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left" sortDirection={orderBy === 'subAccount' ? order : false}>
                                    <TableSortLabel active={orderBy === 'subAccountName'}
                                        direction={orderBy === 'subAccountName' ? order : 'asc'}
                                        onClick={() => handleColumnSort('subAccountName')}>
                                        {t('subAccount')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'aggregateShareDifficulty'}
                                        direction={orderBy === 'aggregateShareDifficulty' ? order : 'asc'}
                                        onClick={() => handleColumnSort('aggregateShareDifficulty')}>
                                        {t('aggregateShareDifficulty')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'convertedHashrate'}
                                        direction={orderBy === 'convertedHashrate' ? order : 'asc'}
                                        onClick={() => handleColumnSort('convertedHashrate')}>
                                        {t('hashrate')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'networkDifficulty'}
                                        direction={orderBy === 'networkDifficulty' ? order : 'asc'}
                                        onClick={() => handleColumnSort('networkDifficulty')}>
                                        {t('networkDifficulty')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'ppsBaseAmount'}
                                        direction={orderBy === 'ppsBaseAmount' ? order : 'asc'}
                                        onClick={() => handleColumnSort('ppsBaseAmount')}>
                                        {t('ppsBaseAmount')} (ZEC)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'totalNetworkBlocks'}
                                        direction={orderBy === 'totalNetworkBlocks' ? order : 'asc'}
                                        onClick={() => handleColumnSort('totalNetworkBlocks')}>
                                        {t('totalNetworkBlocks')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'totalBlocksUsed'}
                                        direction={orderBy === 'totalBlocksUsed' ? order : 'asc'}
                                        onClick={() => handleColumnSort('totalBlocksUsed')}>
                                        {t('totalBlocksUsed')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    Details
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'fppsRatePercent'}
                                        direction={orderBy === 'fppsRatePercent' ? order : 'asc'}
                                        onClick={() => handleColumnSort('fppsRatePercent')}>
                                        {t('fppsRate')} (%)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'txFeeRewardAmount'}
                                        direction={orderBy === 'txFeeRewardAmount' ? order : 'asc'}
                                        onClick={() => handleColumnSort('txFeeRewardAmount')}>
                                        {t('txFeeReward')} (ZEC)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'grossEarnings'}
                                        direction={orderBy === 'grossEarnings' ? order : 'asc'}
                                        onClick={() => handleColumnSort('grossEarnings')}>
                                        {t('grossEarnings')} (BTC)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'poolFeeAmount'}
                                        direction={orderBy === 'poolFeeAmount' ? order : 'asc'}
                                        onClick={() => handleColumnSort('poolFeeAmount')}>
                                        {t('poolFeeAmount')} (ZEC)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                    <TableSortLabel active={orderBy === 'earned'}
                                        direction={orderBy === 'earned' ? order : 'asc'}
                                        onClick={() => handleColumnSort('earned')}>
                                        {t('totalAmount')} (ZEC)
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && sortedRows?.length > 0
                                ? (rowsPerPage > 0
                                    ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sortedRows).map((row, index) => {
                                        return <TableRow key={index} style={{ height: '50px' }}>
                                            <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>{row.year}</TableCell>
                                            <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>{row.startDate}</TableCell>
                                            <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>{row.endDate}</TableCell>
                                            <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>{row.groupName}</TableCell>
                                            <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>{row.subAccountName}</TableCell>
                                            <TableCell align="left">{row.formattedAggregatedDifficulty}</TableCell>
                                            <TableCell align="left">{row.hashrate}</TableCell>
                                            <TableCell align="left">{row.networkDifficultyFormatted}</TableCell>
                                            <TableCell align="left">{row.ppsBaseAmount}</TableCell>
                                            <TableCell align="left">{row.totalNetworkBlocks}</TableCell>
                                            <TableCell align="left">{row.totalBlocksUsed}</TableCell>
                                            <TableCell align="left">
                                                <Typography
                                                    className={classes.calculationInfo}
                                                    onClick={() => handleOpenPplnsCalculationDialog(row.startDate)}>
                                                    View
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left">{row.fppsRatePercent}%</TableCell>
                                            <TableCell align="left">{row.txFeeRewardAmount}</TableCell>
                                            <TableCell align="left">{row.grossEarnings}</TableCell>
                                            <TableCell align="left">{row.poolFeeAmount}</TableCell>
                                            <TableCell align="left">
                                                {parseFloat(row.earned).toLocaleString(undefined, { minimumFractionDigits: 8 })}
                                                {!!row.networkDifficultyChanged &&
                                                    (<ClickAwayListener
                                                        onClickAway={() => {
                                                            if (
                                                                showDifficultyChangeInfo &&
                                                                selectedRow === index
                                                            ) {
                                                                displayDifficultyChangeInfo(
                                                                    index,
                                                                    false
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <Tooltip
                                                            placement="top"
                                                            disableFocusListener
                                                            disableHoverListener
                                                            title={
                                                                <Typography variant="h6" style={{ padding: `${theme.spacing(1.0)} 0 ${theme.spacing(1.0)} ${theme.spacing(1.0)}` }}>
                                                                    {t('networkDifficultyChanged')}
                                                                </Typography>
                                                            }
                                                            open={
                                                                selectedRow === index &&
                                                                showDifficultyChangeInfo &&
                                                                !loading
                                                            }
                                                            arrow
                                                        >
                                                            <Info
                                                                size={16}
                                                                fontSize="small"
                                                                style={{
                                                                    marginBottom: theme.spacing(-0.5),
                                                                    marginLeft: theme.spacing(4.0),
                                                                    cursor: 'pointer',
                                                                    color: 'var(--color-text-warning)'
                                                                }}
                                                                onClick={() => {
                                                                    displayDifficultyChangeInfo(
                                                                        index,
                                                                        !showDifficultyChangeInfo
                                                                    );
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    </ClickAwayListener>)
                                                }
                                            </TableCell>
                                        </TableRow>
                                    }) : loading ? (
                                        <TableRow style={{ height: '50px' }}>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                            <TableCell align="left"><Skeleton variant="rounded" height={20} width={"100%"} /></TableCell>
                                        </TableRow>
                                    ) : (
                                    !loading && error ? <TableRow>
                                        <TableCell colSpan={17} style={{ height: rowsPerPage * 5 }} align="center">
                                            {error}
                                        </TableCell>
                                    </TableRow> :
                                        <TableCell colSpan={17} style={{ padding: 0 }}>
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                justifyItems="center"
                                                alignItems='center'
                                                style={{ background: 'var(--color-background)' }}
                                            >
                                                <div style={{ position: "relative" }}>
                                                    <p style={{ position: "absolute", left: 160, top: 280, fontFamily: 'var(--font-family)', fontSize: 16, fontWeight: 600, color: "#e6e5e8" }}>No audit data yet</p>
                                                    <p style={{ position: "absolute", left: 150, top: 310, fontFamily: 'var(--font-family)', fontSize: 14, fontWeight: 400, color: '#9AA4B2' }}>This will get populated later!</p>
                                                    <div style={{ position: "absolute", width: 48, height: 48, left: 217, top: 217, border: '1px solid #364152', borderRadius: 'var(--radius-l)' }}></div>
                                                    <svg viewBox="0 0 25 24" style={{ position: "absolute", left: 229, top: 230 }} width="25" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M21.5 21L18.0001 17.5M20.5 11.5C20.5 16.1944 16.6944 20 12 20C7.30558 20 3.5 16.1944 3.5 11.5C3.5 6.80558 7.30558 3 12 3C16.6944 3 20.5 6.80558 20.5 11.5Z" stroke="#CDD5DF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <svg width="480" height="480" viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="240" cy="240" r="47.5" stroke="#202939" />
                                                        <circle cx="240" cy="240" r="79.5" stroke="#202939" />
                                                        <circle cx="240" cy="240" r="111.5" stroke="#202939" />
                                                        <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.5} />
                                                        <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.4} />
                                                        <circle cx="240" cy="240" r="175.5" stroke="#202939" opacity={0.3} />
                                                        <circle cx="240" cy="240" r="207.5" stroke="#202939" opacity={0.2} />
                                                        <circle cx="240" cy="240" r="239.5" stroke="#202939" opacity={0.1} />
                                                    </svg>
                                                </div>
                                            </Box>
                                        </TableCell>
                                )
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <Table>
                    <TableFooter>
                        <TableRow>
                            <TablePaginationComp
                                filteredRows={sortedRows}
                                count={(!!sortedRows && (!!sortedRows?.length > 0)) ? sortedRows?.length : 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                handleChangePage={handleChangePage}
                                handleChangeRowsPerPage={handleChangeRowsPerPage}
                                disablePagination={loading || error || !sortedRows?.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </>
        </Card><Dialog
            open={openPplnsCalcDialog}
            onClose={handleClosePplnsCalculationDialog}
            maxWidth="lg"
            fullWidth
            slotProps={{
                paper: {
                    style: {
                        backgroundColor: 'var(--neutral-2)',
                        color: 'var(--color-text)',
                    }
                }
            }}
        >
            <DialogTitle style={{ borderBottom: '1px solid var(--color-border)' }}>
                <p className="m-heading is-size-xl" style={{ color: 'var(--core-white)' }}>Calculation Breakdown</p>
                <div
                    style={
                        mediumDevice
                            ? { position: 'absolute', top: '12px', right: '12px', cursor: 'pointer' }
                            : { position: 'absolute', top: '15px', right: '36px', cursor: 'pointer' }
                    }
                    onClick={handleClosePplnsCalculationDialog}
                >
                    <X />
                </div>
            </DialogTitle>
            <DialogContent>
                <PPLNSCalculationBreakdown date={selectedPplnsDetailsDate} onDataLoaded={null} />
            </DialogContent>
        </Dialog>
    </>);
}

export default AuditReportTable;
